
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AIJobInputValues } from "../schemas/aiJobInputSchema";

interface UseAIJobGenerationProps {
  clientId?: string;
  onSuccess?: () => void;
  onClose: () => void;
}

export const useAIJobGeneration = ({ 
  clientId, 
  onSuccess, 
  onClose 
}: UseAIJobGenerationProps) => {
  const { user } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRefining, setIsRefining] = useState(false);

  const generateJobPackage = async (inputData: AIJobInputValues) => {
    if (!user) {
      toast.error("You must be logged in to generate job packages");
      return null;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-comprehensive-job-package', {
        body: {
          ...inputData,
          userId: user.id,
        }
      });

      if (error) {
        console.error("Error generating job package:", error);
        toast.error("Failed to generate job package. Please try again.");
        return null;
      }

      if (!data.success) {
        console.error("API reported error:", data.error);
        toast.error(data.error || "Failed to generate job package");
        return null;
      }

      toast.success("Job package generated successfully!");
      return data.jobPackage;
      
    } catch (error) {
      console.error("Error:", error);
      toast.error("An unexpected error occurred");
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const refineJobPackage = async (currentJob: any, feedback: string) => {
    setIsRefining(true);
    try {
      const { data, error } = await supabase.functions.invoke('refine-job-package', {
        body: {
          currentJob,
          feedback,
          userId: user?.id,
        }
      });

      if (error) {
        console.error("Error refining job package:", error);
        toast.error("Failed to refine job package. Please try again.");
        return null;
      }

      if (!data.success) {
        toast.error(data.error || "Failed to refine job package");
        return null;
      }

      toast.success("Job package refined successfully!");
      return data.refinedJobPackage;
      
    } catch (error) {
      console.error("Error:", error);
      toast.error("An unexpected error occurred during refinement");
      return null;
    } finally {
      setIsRefining(false);
    }
  };

  const acceptJobPackage = async (jobPackage: any) => {
    if (!user) {
      toast.error("You must be logged in to create jobs");
      return;
    }

    setIsAccepting(true);
    try {
      // Convert the generated package to job format
      const jobData = {
        title: jobPackage.title,
        company: jobPackage.company,
        location: jobPackage.location,
        type: jobPackage.type,
        description: jobPackage.description,
        requirements: jobPackage.requirements,
        skills: jobPackage.skills?.mustHave || [],
        salary_min: jobPackage.salaryRange?.min,
        salary_max: jobPackage.salaryRange?.max,
        experience_required: jobPackage.experienceRequired,
        status: "open",
        user_id: user.id,
        client_id: clientId || null,
      };

      const { data, error } = await supabase
        .from('jobs')
        .insert(jobData)
        .select()
        .single();

      if (error) {
        console.error("Error creating job:", error);
        toast.error("Failed to create job. Please try again.");
        return;
      }

      toast.success(`Job "${data.title}" created successfully!`);
      
      if (onSuccess) {
        onSuccess();
      }
      onClose();
      
    } catch (error) {
      console.error("Error:", error);
      toast.error("An unexpected error occurred while creating the job");
    } finally {
      setIsAccepting(false);
    }
  };

  return {
    generateJobPackage,
    refineJobPackage,
    acceptJobPackage,
    isGenerating,
    isRefining,
    isAccepting,
  };
};
